import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ServiceCategories() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', description: '', parent_id: '', status: 'active' });
  const [allCategories, setAllCategories] = useState([]);
  const [showMerge, setShowMerge] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');

  const fetchCategories = () => {
    if (!companyId) return;
    setLoading(true);
    let url = `${API}/companies/${companyId}/services/categories`;
    const params = [];
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    if (statusFilter) params.push(`status=${statusFilter}`);
    if (params.length) url += '?' + params.join('&');

    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCategories(Array.isArray(d) ? d : []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  };

  const fetchAll = () => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/services/categories`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setAllCategories(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  useEffect(() => { fetchCategories(); fetchAll(); }, [companyId]);

  const handleSubmit = () => {
    const method = editing ? 'PUT' : 'POST';
    const url = editing
      ? `${API}/companies/${companyId}/services/categories/${editing.id}`
      : `${API}/companies/${companyId}/services/categories`;
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setShowForm(false); setEditing(null); resetForm(); fetchCategories(); fetchAll(); } })
      .catch(() => {});
  };

  const resetForm = () => setForm({ name: '', code: '', description: '', parent_id: '', status: 'active' });

  const openEdit = (c) => {
    setForm({ name: c.name || '', code: c.code || '', description: c.description || '', parent_id: c.parent_id || '', status: c.status || 'active' });
    setEditing(c);
    setShowForm(true);
  };

  const viewDetail = (id) => {
    fetch(`${API}/companies/${companyId}/services/categories/${id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setDetail(d))
      .catch(() => {});
  };

  const doAction = (id, action) => {
    fetch(`${API}/companies/${companyId}/services/categories/${id}/${action}`, { method: 'POST', headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(() => { fetchCategories(); if (detail && detail.id === id) viewDetail(id); })
      .catch(() => {});
  };

  const doMerge = (sourceId) => {
    if (!mergeTarget) return;
    fetch(`${API}/companies/${companyId}/services/categories/${sourceId}/merge`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ target_id: mergeTarget }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(() => { setShowMerge(null); setMergeTarget(''); fetchCategories(); fetchAll(); })
      .catch(() => {});
  };

  const doDelete = (id) => {
    if (!window.confirm('Delete this category?')) return;
    fetch(`${API}/companies/${companyId}/services/categories/${id}`, { method: 'DELETE', headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(() => fetchCategories())
      .catch(() => {});
  };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const cardStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
  const btnStyle = (bg) => ({ padding: '4px 12px', background: bg, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', marginRight: 4 });
  const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
  const badgeStyle = (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: color + '20', color });
  const statusColor = { active: '#22c55e', inactive: '#f59e0b', archived: '#6b7280' };

  if (detail) {
    return (
      <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <button onClick={() => setDetail(null)} style={{ ...btnStyle('#6b7280'), marginBottom: 16, padding: '6px 16px' }}>← Back</button>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>{detail.name}</h2>
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{detail.code}</span>
              <span style={{ ...badgeStyle(statusColor[detail.status] || '#94a3b8'), marginLeft: 12 }}>{detail.status}</span>
              {detail.parent && <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 4 }}>Parent: {detail.parent.name}</div>}
            </div>
            <div>
              {detail.status === 'active' && <button onClick={() => doAction(detail.id, 'archive')} style={btnStyle('#6b7280')}>Archive</button>}
              {detail.status === 'archived' && <button onClick={() => doAction(detail.id, 'restore')} style={btnStyle('#3b82f6')}>Restore</button>}
            </div>
          </div>
          {detail.description && <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#475569' }}>{detail.description}</p>}
        </div>

        {/* Roll-up Analytics */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div style={{ ...cardStyle, borderLeft: '4px solid #3b82f6' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Services</div><div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{detail.services_count || 0}</div></div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #8b5cf6' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Active Engagements</div><div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{detail.active_engagements || 0}</div></div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #22c55e' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Collected Revenue</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#22c55e' }}>{fmt(detail.collected_revenue)}</div></div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #f59e0b' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Margin</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: detail.operational_margin >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(detail.operational_margin)}</div></div>
        </div>

        {/* Sub-categories */}
        {Array.isArray(detail.children) && detail.children.length > 0 && (
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>📂 Sub-categories</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>Name</th><th style={thStyle}>Code</th><th style={thStyle}>Status</th></tr></thead>
              <tbody>
                {detail.children.map(ch => (
                  <tr key={ch.id} style={{ cursor: 'pointer' }} onClick={() => viewDetail(ch.id)}>
                    <td style={{ ...tdStyle, color: '#3b82f6', fontWeight: 600 }}>{ch.name}</td>
                    <td style={tdStyle}>{ch.code}</td>
                    <td style={tdStyle}><span style={badgeStyle(statusColor[ch.status] || '#94a3b8')}>{ch.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Services in this category */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>📦 Services</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>Service</th><th style={thStyle}>Status</th><th style={thStyle}>Active Eng.</th><th style={thStyle}>Revenue</th></tr></thead>
            <tbody>
              {Array.isArray(detail.services) && detail.services.map(s => (
                <tr key={s.id}>
                  <td style={tdStyle}><span style={{ fontWeight: 600 }}>{s.name}</span><br /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{s.service_code}</span></td>
                  <td style={tdStyle}><span style={badgeStyle(statusColor[s.status] || '#94a3b8')}>{s.status}</span></td>
                  <td style={tdStyle}>{s.active_engagements || 0}</td>
                  <td style={tdStyle}>{fmt(s.collected_revenue)}</td>
                </tr>
              ))}
              {(!detail.services || detail.services.length === 0) && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No services</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>📂 Service Categories</h2>
        <button onClick={() => { resetForm(); setEditing(null); setShowForm(true); }} style={{ padding: '8px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>+ New Category</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input placeholder="Search categories..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchCategories()} style={{ ...inputStyle, maxWidth: 260 }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, maxWidth: 160 }}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
        <button onClick={fetchCategories} style={{ ...btnStyle('#3b82f6'), padding: '8px 16px' }}>Search</button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 480, maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '1.2rem', fontWeight: 700 }}>{editing ? 'Edit Category' : 'New Category'}</h3>
            <div style={{ display: 'grid', gap: 14 }}>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Code</label><input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} style={inputStyle} placeholder="Auto-generated if empty" /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Parent Category</label>
                <select value={form.parent_id} onChange={e => setForm({ ...form, parent_id: e.target.value })} style={inputStyle}>
                  <option value="">— Root Level —</option>
                  {allCategories.filter(c => !editing || c.id !== editing.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ ...btnStyle('#6b7280'), padding: '8px 20px' }}>Cancel</button>
              <button onClick={handleSubmit} style={{ ...btnStyle('#3b82f6'), padding: '8px 20px' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Modal */}
      {showMerge && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 400 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700 }}>Merge "{showMerge.name}" into:</h3>
            <select value={mergeTarget} onChange={e => setMergeTarget(e.target.value)} style={inputStyle}>
              <option value="">— Select target —</option>
              {allCategories.filter(c => c.id !== showMerge.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowMerge(null); setMergeTarget(''); }} style={{ ...btnStyle('#6b7280'), padding: '8px 20px' }}>Cancel</button>
              <button onClick={() => doMerge(showMerge.id)} style={{ ...btnStyle('#ef4444'), padding: '8px 20px' }}>Merge</button>
            </div>
          </div>
        </div>
      )}

      {/* Categories Table */}
      {initialLoading ? <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div> : (
        <div style={cardStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Parent</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Services</th>
                <th style={thStyle}>Engagements</th>
                <th style={thStyle}>Revenue</th>
                <th style={thStyle}>Margin</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <tr key={c.id}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => viewDetail(c.id)}>{c.name}</span>
                    <br /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{c.code}</span>
                    {c.children_count > 0 && <span style={{ ...badgeStyle('#8b5cf6'), marginLeft: 8 }}>{c.children_count} sub</span>}
                  </td>
                  <td style={tdStyle}>{c.parent?.name || '—'}</td>
                  <td style={tdStyle}><span style={badgeStyle(statusColor[c.status] || '#94a3b8')}>{c.status}</span></td>
                  <td style={tdStyle}>{c.services_count || 0}</td>
                  <td style={tdStyle}>{c.active_engagements || 0}</td>
                  <td style={tdStyle}>{fmt(c.collected_revenue)}</td>
                  <td style={{ ...tdStyle, color: c.operational_margin >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>{fmt(c.operational_margin)}</td>
                  <td style={tdStyle}>
                    <button onClick={() => openEdit(c)} style={btnStyle('#f59e0b')}>Edit</button>
                    <button onClick={() => setShowMerge(c)} style={btnStyle('#8b5cf6')}>Merge</button>
                    <button onClick={() => doDelete(c.id)} style={btnStyle('#ef4444')}>Delete</button>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No categories found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
